import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({ subject: '', body: '' });

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/hosts/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const token = localStorage.getItem('quizzler_host_token');
      await fetch(`${BACKEND_URL}/api/hosts/messages/${messageId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setMessages(messages.map(msg => 
        msg.id === messageId ? { ...msg, is_read: true } : msg
      ));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleMessageClick = (message) => {
    setSelectedMessage(message);
    setReplyText('');
    if (!message.is_read) {
      markAsRead(message.id);
    }
  };

const handleDeleteMessage = async (messageId) => {
  if (!window.confirm('Are you sure you want to delete this message? This cannot be undone.')) {
    return;
  }

  try {
    const token = localStorage.getItem('quizzler_host_token');
    const response = await fetch(`${BACKEND_URL}/api/hosts/messages/${messageId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.ok) {
      alert('✅ Message deleted');
      setMessages(messages.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
    } else {
      alert('Failed to delete message');
    }
  } catch (error) {
    console.error('Error deleting message:', error);
    alert('Failed to delete message');
  }
};

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    setSending(true);
    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/hosts/messages/${selectedMessage.id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ body: replyText })
      });

      if (response.ok) {
        alert('✅ Reply sent successfully!');
        setReplyText('');
        fetchMessages();
      } else {
        alert('Failed to send reply');
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleContactAdmin = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      const token = localStorage.getItem('quizzler_host_token');
      const response = await fetch(`${BACKEND_URL}/api/hosts/contact-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        alert('✅ Message sent to admin!');
        setContactForm({ subject: '', body: '' });
        setShowContactForm(false);
        fetchMessages();
      } else {
        alert('Failed to send message');
      }
    } catch (error) {
      console.error('Error contacting admin:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold text-teal-600">Messages</h1>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  {unreadCount} Unread
                </span>
              )}
            </div>
            <button
              onClick={() => setShowContactForm(!showContactForm)}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-semibold transition"
            >
              {showContactForm ? 'Cancel' : '✉️ Contact Admin'}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Contact Admin Form */}
        {showContactForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Contact Admin</h2>
            <form onSubmit={handleContactAdmin}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="What's this about?"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Message *
                </label>
                <textarea
                  value={contactForm.body}
                  onChange={(e) => setContactForm({ ...contactForm, body: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Your message to admin..."
                  rows="4"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-3 px-4 rounded-lg transition disabled:opacity-50"
              >
                {sending ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Message List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-teal-600 text-white px-4 py-3 font-semibold">
                Inbox
              </div>
              
              {loading ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : messages.length === 0 ? (
                <div className="p-4 text-center text-gray-500">No messages</div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {messages.map((message) => (
                   <button
  key={message.id}
  onClick={() => handleMessageClick(message)}
  className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition ${
    selectedMessage?.id === message.id ? 'bg-teal-50' : ''
  } ${!message.is_read ? 'font-semibold' : ''}`}
>
  <div className="flex items-center justify-between mb-1">
    <span className="text-sm text-gray-800">
      {message.subject}
    </span>
    <div className="flex items-center gap-2">
      {!message.is_read && (
        <span className="w-2 h-2 bg-teal-600 rounded-full"></span>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteMessage(message.id);
        }}
        className="text-red-500 hover:text-red-700 text-xs"
        title="Delete message"
      >
        🗑️
      </button>
    </div>
  </div>
  <div className="text-xs text-gray-500">
    {new Date(message.created_at).toLocaleDateString()}
  </div>
</button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Message Detail + Reply */}
          <div className="lg:col-span-2">
            {selectedMessage ? (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    {selectedMessage.subject}
                  </h2>
                  <div className="text-sm text-gray-600">
                    From: Quizzler Admin
                    <br />
                    Date: {new Date(selectedMessage.created_at).toLocaleString()}
                  </div>
                </div>
                
                <div className="text-gray-700 whitespace-pre-wrap mb-6">
                  {selectedMessage.body}
                </div>

                {/* Reply Form */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="font-bold text-gray-800 mb-3">Reply</h3>
                  <form onSubmit={handleReply}>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 mb-3"
                      placeholder="Type your reply..."
                      rows="4"
                      required
                    />
                    <button
                      type="submit"
                      disabled={sending}
                      className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg font-semibold transition disabled:opacity-50"
                    >
                      {sending ? 'Sending...' : 'Send Reply'}
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-500">
                Select a message to read and reply
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
